/**
 * API utility functions with cross-domain authentication support
 * 
 * This module provides fetch wrappers that automatically include:
 * - credentials: 'include' for same-origin cookie auth
 * - Authorization header from localStorage for cross-domain auth
 */

const SESSION_TOKEN_KEY = 'gs_session_token';

/**
 * Get authentication headers for API requests
 * Supports both cookie-based (same origin) and token-based (cross-domain) auth
 */
export const getAuthHeaders = () => {
  const token = localStorage.getItem(SESSION_TOKEN_KEY);
  if (token) {
    return { 'Authorization': `Bearer ${token}` };
  }
  return {};
};

/**
 * Normalise any accidental `//api` → `/api` collisions that happen when
 * REACT_APP_BACKEND_URL is configured with a trailing slash (e.g.
 * `https://workflow.gsimaging.com.au/` on production). The resulting
 * `${URL}/api/...` becomes `//api/...` which Cloudflare's edge does NOT
 * route to the backend — it falls through to the SPA index.html and the
 * caller chokes on "Unexpected token '<', '<!doctype...'".
 *
 * Splits the protocol off so we don't accidentally collapse `https://`
 * into `https:/`. Idempotent — safe to call on already-clean URLs.
 */
const normaliseUrl = (url) => {
  if (typeof url !== 'string') return url;
  return url.replace(/^(https?:\/\/)?(.*)$/, (_m, proto = '', rest) => {
    return (proto || '') + rest.replace(/\/{2,}/g, '/');
  });
};

/**
 * Authenticated fetch wrapper
 * Automatically includes credentials and auth headers
 */
export const authFetch = async (url, options = {}) => {
  const headers = {
    ...getAuthHeaders(),
    ...options.headers,
  };

  return fetch(normaliseUrl(url), {
    ...options,
    credentials: 'include',
    headers,
  });
};

/**
 * Authenticated JSON POST/PUT/PATCH request
 */
export const authJsonFetch = async (url, options = {}) => {
  const headers = {
    'Content-Type': 'application/json',
    ...getAuthHeaders(),
    ...options.headers,
  };

  return fetch(normaliseUrl(url), {
    ...options,
    credentials: 'include',
    headers,
  });
};
